import { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import YourTabSkeleton from 'components/YourTabSkeleton/Index'
import {
  paymentEnableData,
  isPaymentStatusLoading,
} from 'features/restaurant/restaurant'
import { tabDetail } from 'features/tab/tab'
import OrderConfirmation from '../OrderConfirmation/Index'
import YourTab from './Index'

function YourTabWrapper() {
  const [loading, setLoading] = useState(true)
  const [hasPlacedOrder, setHasPlacedOrder] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  // Get payment status from Redux to ensure health checks are respected
  const isPaymentEnable = useSelector(paymentEnableData)
  const paymentStatusLoading = useSelector(isPaymentStatusLoading)
  const tabDetailData = useSelector(tabDetail)

  const checkForPlacedOrders = useCallback(async (attemptNumber = 0) => {
    const MAX_RETRIES = 3
    const RETRY_DELAY = 1000 // 1 second

    try {
      setLoading(true)
      if (attemptNumber === 0) {
        setError(null)
        setRetryCount(0)
      }

      // Get table and establishment data from session storage
      const tableId = sessionStorage.getItem('posTableId')
      const establishmentId = sessionStorage.getItem('establishmentId')

      if (!tableId || !establishmentId) {
        console.warn('Missing tableId or establishmentId for order check', {
          tableId,
          establishmentId,
        })
        setHasPlacedOrder(false)
        return
      }

      console.log('Checking for placed orders with caching...', {
        tableId: Number(tableId),
        establishmentId: Number(establishmentId),
        attempt: attemptNumber + 1,
      })

      // OPTIMIZATION: Use cached hasActiveOrder method to reduce duplicate API calls
      const { OrderConfirmationService } = await import(
        '../../services/order-confirmation.service'
      )
      const hasFasTabOrder = await OrderConfirmationService.hasActiveOrder()

      console.log('Cached order check result:', {
        hasFasTabOrder,
        source: 'hasActiveOrder-with-cache',
        attempt: attemptNumber + 1,
      })

      setHasPlacedOrder(hasFasTabOrder)
      setError(null) // Clear any previous errors on success
    } catch (errorCatch) {
      console.error(
        `Error checking for placed orders (attempt ${attemptNumber + 1}):`,
        errorCatch
      )

      if (attemptNumber < MAX_RETRIES) {
        setRetryCount(attemptNumber + 1)
        console.log(
          `Retrying in ${RETRY_DELAY}ms... (${
            attemptNumber + 1
          }/${MAX_RETRIES})`
        )

        setTimeout(() => {
          checkForPlacedOrders(attemptNumber + 1)
        }, RETRY_DELAY)
        return
      }

      // All retries exhausted
      console.error('All retry attempts failed, falling back to YourTab')
      setError('Failed to load order information after multiple attempts')
      setHasPlacedOrder(false)
    } finally {
      // Only set loading to false if this is the final attempt (success or final failure)
      if (attemptNumber === 0 || attemptNumber >= MAX_RETRIES) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    checkForPlacedOrders()
  }, [checkForPlacedOrders])

  // Reset order state when table is closed via SignalR
  useEffect(() => {
    if (!tabDetailData && hasPlacedOrder !== null) {
      setHasPlacedOrder(false)
      setLoading(false)
      setError(null)
    }
  }, [tabDetailData, hasPlacedOrder])

  // CRITICAL: Don't require tabDetailData when table is closed (hasPlacedOrder = false)
  const isTableClosed = !tabDetailData && hasPlacedOrder === false
  const isStillLoading =
    loading ||
    paymentStatusLoading ||
    hasPlacedOrder === null ||
    (!tabDetailData && !isTableClosed)

  if (isStillLoading) {
    return <YourTabSkeleton />
  }

  if (error) {
    // Show retry information in console for debugging
    if (retryCount > 0) {
      console.log(
        `Failed after ${retryCount} retry attempts, showing YourTab fallback`
      )
    }
    return <YourTab />
  }

  const isTableLocked =
    !isPaymentEnable?.status ||
    isPaymentEnable?.isTableDisable ||
    !isPaymentEnable?.isHealthy ||
    tabDetailData?.tabPayments?.isPosPaid ||
    tabDetailData?.isCheckChanged

  if (isTableLocked) {
    return <YourTab />
  }

  return hasPlacedOrder ? <OrderConfirmation /> : <YourTab />
}

export default YourTabWrapper
